import '../../styles/_modal.scss';
import '../../styles/_request-credit-panel.scss';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import LargePanelContext from '../../contexts/LargePanelContext';
import ModalPanelContext from '../../contexts/ModalPanelContext';
import { useSubmitCreditRequestMutation } from '../../datasource/mutations/creditSubmission';
import { MenuOptions } from '../../enums/MenuOptions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FetchType, useAnalytics } from '../../hooks/useAnalytics';
import useCpeExams from '../../hooks/useCpeExams';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { useRequestCredit } from '../../hooks/useRequestCredit';
import { useScrollForMoreDetails } from '../../hooks/useScrollForMoreDetails';
import AuthCode from '../AuthCode/AuthCode';
import Button from '../Button/Button';
import { ErrorContainer } from '../MainError/MainError';
import withPLIErrorBoundary from '../PliErrorBoundary/PliErrorBoundary';
import ProgramEvaluationPanel from '../ProgramEvaluationPanel/ProgramEvaluationPanel';
import RequestCreditCpeBlock from '../RequestCreditCpeBlock/RequestCreditCpeBlock';
import RequestCreditLicenses from '../RequestCreditLicenses/RequestCreditLicenses';
import { RequestCreditPlayerTimeCards } from '../RequestCreditPlayerTimeCards/RequestCreditPlayerTimeCards';
import { RequestCreditTimeCards } from '../RequestCreditTimeCards/RequestCreditTimeCards';
import { setIsExamStatusVisible, setIsSideBarDisabled, setLargePanelOpenCommand } from '../Store/menuSlice';
import { setEncryptedRegistrationId, setLastSubmittedEncryptedRegistrationId } from '../Store/requestCreditSlice';

export type IRequestCreditPanelProps = {
    standalone?: boolean;
    onClose: (() => void) | null;
};

const requestCreditSubmitErrorId = 'request-credit-submit-error';

export const RequestCreditPanelBase = ({ onClose, standalone } : IRequestCreditPanelProps) => {
    const dispatch = useAppDispatch();
    const { exams: cpeExams, isGetExamsError } = useCpeExams();
    const isPortable = useAppSelector((state) => state.requestCredit.isPortable);
    const programTitle = useAppSelector((state) => state.requestCredit.programTitle);
    const { pushToDataLayer } = useAnalytics();
    
    useEffect(() => {
        dispatch(setIsExamStatusVisible({ RequestCredit : true }));

        return () => {
            dispatch(setIsExamStatusVisible({ RequestCredit : false }));
        };
    }, [dispatch]);

    const { 
        messages, 
        isContinueButtonDisabled, 
        errorMessageElementIds, 
        creditSubmissionProps, 
        isGetLicensesError 
    } = useRequestCredit();
    const [currentStep, setCurrentStep] = useState<'credits' | 'survey'>('credits');

    const { showBoundary } = useErrorBoundary();

    const [hasTimeCardsLoaded, setHasTimeCardsLoaded] = useState<boolean>(false);

    const [submitCreditRequest, { isError, isLoading }] = useSubmitCreditRequestMutation();

    const [isCreditRequestSubmitError, setIsCreditRequestSubmitError] = useState(false);
    const onClickTimeout = 1600;
    const [isClicked, setIsClicked] = useState<boolean>(false);

    const sendCreditRequest = useCallback(async () => {
        if (!creditSubmissionProps) return;

        setIsCreditRequestSubmitError(false);
        setIsClicked(true);

        try {
            const result = await submitCreditRequest(creditSubmissionProps).unwrap();
            if (result.encryptedRegistrationId) {
                dispatch(setIsSideBarDisabled(false));
                dispatch(setLargePanelOpenCommand(MenuOptions.CreditSubmissionConfirmation));
                pushToDataLayer({ event: 'request_credit_complete', fetchType: FetchType.Program });

                dispatch(setLastSubmittedEncryptedRegistrationId(creditSubmissionProps.encryptedRegistrationId));
                dispatch(setEncryptedRegistrationId(result.encryptedRegistrationId));
                setCurrentStep('survey');

                setTimeout(() => {
                    setIsClicked(false);
                }, onClickTimeout);
            } else {
                setIsCreditRequestSubmitError(true);
                setIsClicked(false);
            }
        } catch {
            setIsCreditRequestSubmitError(true);
            setIsClicked(false);
        }
    }, [creditSubmissionProps, dispatch, pushToDataLayer, submitCreditRequest]);

    const onClickContinueButton = async () => { 
        if (isContinueButtonDisabled) {
            return;
        }
        if (!creditSubmissionProps) {
            return;
        }
        setCurrentStep('survey');
    };

    useEffect(() => {
        if (isGetLicensesError || isGetExamsError) {
            showBoundary(new Error('Error fetching credit request data'));
        }
    }, [isGetLicensesError, isGetExamsError, showBoundary]);

    const [isLicensesLoaded, setIsLicensesLoaded] = useState(false);

    const largePanelContainerRef = useContext(LargePanelContext);
    const modalPanelContainerRef = useContext(ModalPanelContext);

    const {
        ref: scrollRefs,
        showScrollForMoreDetails,
        scrollToTargetElement,
    } = useScrollForMoreDetails<HTMLDivElement, HTMLParagraphElement, HTMLDivElement>();

    useMergeRefs(scrollRefs.container)
        .invoke(largePanelContainerRef?.current ?? modalPanelContainerRef?.current);

    if (currentStep === 'survey') {
        return <ProgramEvaluationPanel onSubmit={sendCreditRequest} />;
    }

    return (
        <>
            <div className='watch-modal__content request-credit'>

                <div className='large-panel__heading request-credit__heading'>
                    <h3 className='heading-4 mb-0'>{messages.title}</h3>
                    <p className='paragraph-1 text--medium-grey mb-0'>{messages.subTitle}</p>
                    {standalone && <div className='heading-6--medium mt-4'>{programTitle}</div>}
                </div>

                {standalone ? <RequestCreditTimeCards onTimeCardsLoaded={setHasTimeCardsLoaded} onClose={onClose} /> : <RequestCreditPlayerTimeCards onTimeCardsLoaded={setHasTimeCardsLoaded} onClose={onClose} />}
                
                {
                    cpeExams && cpeExams.length > 0 &&
                    <RequestCreditCpeBlock />
                }

                <RequestCreditLicenses onLicensesLoaded={setIsLicensesLoaded} />

                {isPortable && isLicensesLoaded && <AuthCode />}
            
                <p className='paragraph-2 text--medium-grey mb-0' ref={scrollRefs.target}>
                    {isPortable ? messages.acknowledgementPortable : messages.acknowledgement}
                </p>
            
            </div>
            <div className='watch-modal__footer' ref={scrollRefs.footer}>
                <div className='watch-modal__footer__button-container'>
                    {
                        (isCreditRequestSubmitError || isError) && 
                        <div className='pb-1'>
                            <i className='fa-kit fa-alert pr-1 text--system-red' aria-hidden={true}/>
                            <span id={requestCreditSubmitErrorId} className='sentence-label text--system-red'>
                                There was a problem processing your request. Please try again later.
                            </span>
                        </div>
                    }
                    <Button 
                        buttonClass={!showScrollForMoreDetails && isContinueButtonDisabled ? 'sticky-button sticky-button--disabled' : 'sticky-button'}
                        label={showScrollForMoreDetails ? 'Scroll for more details' : 'Continue'}
                        aria-label={showScrollForMoreDetails ? 'Scroll for more details' : 'Continue to request credit'}
                        action={showScrollForMoreDetails ? scrollToTargetElement : onClickContinueButton}
                        icon={showScrollForMoreDetails ? 'chevron-down' : 'forward-arrow'}
                        iconPosition={showScrollForMoreDetails ? 'left' : 'right'}
                        isLoading={(!isLicensesLoaded ||!hasTimeCardsLoaded) || isLoading || isClicked}
                        disabled={!isLicensesLoaded || !hasTimeCardsLoaded}
                        aria-describedby={showScrollForMoreDetails ? 
                            '' : 
                            [...errorMessageElementIds, (isError || isCreditRequestSubmitError) ? requestCreditSubmitErrorId : ''].join(' ')
                        }
                    />
                </div>
            </div>
        </>
    );
};

/**
 * Default export: component already wrapped with the Error Boundary HOC.
 * - Uses MainError as fallback (SmallPanel layout).
 * - No centering wrapper will be applied to custom fallbacks (per HOC behavior).
 */
export const RequestCreditPanel = withPLIErrorBoundary(RequestCreditPanelBase, null, {
    errorContainer: ErrorContainer.LargePanel,
    title: 'Sorry, we can’t load the request credit panel right now.',
});

export default RequestCreditPanel;